import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { ConversationDetails } from "@/components/conversation/ConversationDetails";
import { DocumentDetailsDrawer } from "@/components/knowledge-base";
import { Activity } from "@/types/activity";
import { useActivityDialog } from "../hooks/useActivityDialog";
import {
  Agent,
  Document,
  createMinimalAgent,
  createMinimalConversation,
  findAgent,
  findDocument,
} from "../utils/activityResourceUtils";
import { extractBrandingData } from "../utils/brandingUtils";
import { formatRelativeTime } from "../utils/dateUtils";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

interface CompactActivityRowProps {
  activity: Activity;
  agents?: Agent[];
  documents?: Document[];
}

const CATEGORY_ICON: Record<string, GenieIconName> = {
  genie: "genie-lamp",
  conversation: "chat-check",
  document: "files",
  account: "user",
  user_management: "users",
  security: "lock",
  system: "settings",
  settings: "settings",
  general: "activity",
};

const CompactActivityRow = ({ activity, agents = [], documents = [] }: CompactActivityRowProps) => {
  const navigate = useNavigate();
  const { dialogs, selected, openDialog, closeDialog } = useActivityDialog();

  const agent = findAgent(activity, agents);
  const document = findDocument(activity, documents);
  const branding = extractBrandingData(agent);
  const brandColor = branding.hasBranding ? branding.brandColor : "#4E9CFF";
  const genieIcon = CATEGORY_ICON[activity.category as string] ?? "activity";

  const isHighPriority = activity.priority === "high" || activity.priority === "critical";
  const isFailed = activity.status === "failed";

  const handleOpen = useCallback(() => {
    switch (activity.resource_type) {
      case "genie": {
        const target = agent || createMinimalAgent(activity);
        if (target.id) navigate(`/genie-hub/my-genies/${target.id}`, { state: { genie: target } });
        break;
      }
      case "conversation":
        if (activity.resource_id && !activity.resource_id.startsWith("conv")) {
          openDialog("conversation", createMinimalConversation(activity));
        }
        break;
      case "document":
        if (document) openDialog("document", document);
        break;
      default:
        break;
    }
  }, [activity, agent, document, navigate, openDialog]);

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9FAFC] transition-colors text-left group"
      >
        {/* Tiny brand dot + icon */}
        <div className="relative flex-shrink-0">
          <div
            className="h-9 w-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${brandColor}14` }}
          >
            <GenieIcon name={genieIcon} size={20} />
          </div>
          {(isHighPriority || isFailed) && (
            <span
              className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white ${
                isFailed ? "bg-red-500" : "bg-genie-secondary"
              }`}
              aria-hidden
            />
          )}
        </div>

        {/* Title + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 truncate">{activity.title}</span>
            {activity.resource_name && activity.resource_type !== "document" && (
              <span
                className="text-[11px] px-1.5 py-0.5 rounded-md font-medium truncate max-w-[180px]"
                style={{
                  backgroundColor: `${brandColor}14`,
                  color: brandColor,
                }}
              >
                {activity.resource_name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
            <span>{formatRelativeTime(activity.created_at)}</span>
            <span className="text-gray-300">·</span>
            <span className="capitalize">{String(activity.category).replace("_", " ")}</span>
            {activity.status && activity.status !== "completed" && (
              <>
                <span className="text-gray-300">·</span>
                <span
                  className={
                    activity.status === "failed"
                      ? "text-red-600 font-medium"
                      : "text-gray-600"
                  }
                >
                  {activity.status.replace("_", " ")}
                </span>
              </>
            )}
          </div>
        </div>

        <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 group-hover:text-genie-primary transition-colors" />
      </button>

      {selected.document && (
        <DocumentDetailsDrawer
          document={selected.document}
          isOpen={dialogs.document}
          onClose={() => closeDialog("document")}
          onSendToKB={() => {}}
          onRemoveFromKB={() => {}}
        />
      )}

      {selected.conversation && (
        <ConversationDetails
          conversation={selected.conversation}
          isOpen={dialogs.conversation}
          onClose={() => closeDialog("conversation")}
        />
      )}
    </>
  );
};

export default CompactActivityRow;
